/**
 * Gallery axis strategy for 'run' mode.
 *
 * Displays one row per run, with step and index controlled via sliders.
 */

import {getStepFromSliderStep} from '../../utils/stepUtils';
import {GridRow, SlotRegistry, TransformInput} from '../types';
import {createCellsForMediaKeys} from '../utils/cells';
import {caption, indexSlider, label, stepSlider} from '../utils/slots';
import {GalleryAxisStrategy} from './GalleryAxisStrategy';

export class RunAxisStrategy implements GalleryAxisStrategy {
  getAxisValues(input: TransformInput, maxItems?: number): number[] {
    const {runs} = input;
    const runsToProcess = maxItems
      ? Math.min(runs.length, maxItems)
      : runs.length;

    // Return run indices
    const axisValues: number[] = [];
    for (let i = 0; i < runsToProcess; i++) {
      axisValues.push(i);
    }

    return axisValues;
  }

  createRows(input: TransformInput, axisValues: number[]): GridRow[] {
    const {runs, stepMeta, currentIndex, config, allMasks} = input;
    const {mediaKeys} = config;
    const currentStep = input.currentStep ?? 0;

    if (!mediaKeys || mediaKeys.length === 0) {
      return [];
    }

    const rows: GridRow[] = [];

    for (const runIndex of axisValues) {
      const run = runs[runIndex];
      const runStepMeta = stepMeta.runs[runIndex];

      // Snap the current step to this run's available steps
      // IMPORTANT: Always snap for tile lookup, regardless of snapToExistingStep
      // The snapToExistingStep setting only affects slider display, not tile lookup
      const {step: snappedStep, sliderStep} = getStepFromSliderStep(
        runStepMeta,
        currentStep,
        true // Always snap for tile lookup
      );

      const cells = createCellsForMediaKeys(
        run,
        snappedStep,
        currentIndex,
        mediaKeys,
        config,
        allMasks,
        sliderStep
      );

      if (cells.length > 0) {
        rows.push({
          cells,
          rowKey: `run-${run.name}`,
          rowLabel: run.displayName || run.name,
        });
      }
    }

    return rows;
  }

  buildSlotRegistry(): SlotRegistry {
    // Run axis: runs shown on each tile, step and index are controlled
    return {
      'tile-header': [label('run')],
      'tile-footer': [caption()],
      'content-footer': [stepSlider(), indexSlider()],
    };
  }
}
